/**
 * Getting the microphone permission out of the way BEFORE a caller is waiting.
 *
 * The browser only prompts when something asks for the microphone, and for a
 * softphone in a tab the first thing that asks is the answer itself: the SDK
 * calls `getUserMedia` inside `call.answer()`, and holds the SIP 200 OK until
 * it resolves. So an agent who has not granted the microphone yet gets a
 * permission bubble in the corner *while the call is being answered* — and
 * until they click Allow the far end is not connected at all and goes on
 * hearing ringback. Click Block and the call never connects.
 *
 * Asking once when the softphone comes online turns that into a question with
 * no caller on the other end of it, and every answer after it is instant.
 */

export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

/** What the Permissions API says, without asking the agent anything. Firefox
 *  and older Safari don't expose the `microphone` descriptor at all, hence
 *  `unknown` — which means "go and find out", never "denied". */
export async function readMicrophonePermission(): Promise<MicPermission> {
  try {
    const status = await navigator.permissions?.query?.({
      name: 'microphone' as PermissionName,
    });
    if (!status) return 'unknown';
    if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
      return status.state;
    }
    return 'unknown';
  } catch {
    // Unsupported descriptor, or a browser that throws rather than rejecting.
    return 'unknown';
  }
}

/**
 * Hold the microphone open just long enough to settle the permission, then
 * release it.
 *
 * The tracks are stopped immediately: leaving them running lights the tab's
 * recording indicator for as long as the agent is signed in, which reads as the
 * app listening to the room. All we want is the grant.
 *
 * `false` for `promptIfNeeded` checks without ever showing a bubble — for
 * re-reading the state after the agent has been told to fix it.
 */
export async function ensureMicrophoneAccess(promptIfNeeded = true): Promise<MicPermission> {
  const known = await readMicrophonePermission();
  if (known === 'granted' || known === 'denied') return known;
  if (!promptIfNeeded) return known;

  const getUserMedia = navigator.mediaDevices?.getUserMedia;
  if (!getUserMedia) return known;

  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return 'granted';
  } catch (error) {
    // `NotAllowedError` is the agent (or policy) saying no, and is the only one
    // that means "denied". Every other failure — no input device yet, the
    // device busy, Safari refusing a request that came without a user gesture —
    // leaves the question genuinely open, so it must not be recorded as a
    // refusal or we would warn about a microphone that works fine.
    const name = (error as { name?: string } | null)?.name;
    if (name !== 'NotAllowedError') return 'prompt';
    // `NotAllowedError` is ALSO what a dismissed bubble throws — the agent
    // clicked the X rather than Block, which leaves the question open and the
    // browser willing to ask again. Recording that as a refusal puts
    // "Microphone blocked" over a microphone that works. The Permissions API
    // can tell the two apart: it still reads 'prompt' after a dismissal, and
    // 'denied' only after a real block. Where it cannot say (Firefox, older
    // Safari expose no `microphone` descriptor) the refusal stands, because a
    // genuinely blocked microphone going unmentioned is the worse failure.
    const settled = await readMicrophonePermission();
    return settled === 'prompt' ? 'prompt' : 'denied';
  } finally {
    stream?.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        // Already stopped — nothing to release.
      }
    });
  }
}
